// Sync engine: drains the outbox to Supabase, then pulls server-side changes
// back down. Designed to be called whenever the network becomes available,
// when the app foregrounds, and on a light interval.

import { supabase } from "@/integrations/supabase/client";
import {
  bulkUpsertStudentAttendance,
  bulkUpsertTeacherAttendance,
  listOutbox,
  markOutboxFailure,
  removeOutboxEntry,
} from "./localDb";
import { getMeta, setMeta } from "./storage";
import type { CachedStudentAttendance, CachedTeacherAttendance, OutboxEntry } from "./types";
import { errorText, isTransientNetworkError } from "./networkErrors";

type Listener = (state: SyncState) => void;
export interface SyncState {
  online: boolean;
  syncing: boolean;
  pending: number;
  lastSyncedAt: string | null;
  lastError: string | null;
}

let state: SyncState = {
  online: typeof navigator !== "undefined" ? navigator.onLine : true,
  syncing: false,
  pending: 0,
  lastSyncedAt: null,
  lastError: null,
};
const listeners = new Set<Listener>();
let started = false;
let inFlight: Promise<void> | null = null;
let schoolIdGetter: (() => string | null | undefined) | null = null;
// Set when syncNow is called while another sync is already running. Ensures
// rows enqueued after the in-flight sync snapshotted the outbox get pushed
// immediately instead of waiting for the next 60s tick.
let rerunRequested = false;
let rerunSchoolId: string | null = null;

function emit() {
  for (const l of listeners) l(state);
}
function setState(patch: Partial<SyncState>) {
  state = { ...state, ...patch };
  emit();
}

export function getSyncState(): SyncState {
  return state;
}
export function subscribeSync(l: Listener): () => void {
  listeners.add(l);
  l(state);
  return () => listeners.delete(l);
}

async function pushOne(entry: OutboxEntry, schoolId?: string | null): Promise<void> {
  if (entry.op === "upsert_student_attendance") {
    const raw = entry.payload as any;
    const [studentIdFromKey, dateFromKey] = entry.row_key.split("_");
    const payload = {
      student_id: raw.student_id ?? studentIdFromKey,
      school_id: raw.school_id ?? schoolId,
      attendance_date: raw.attendance_date ?? dateFromKey,
      morning_status: raw.morning_status || null,
      afternoon_status: raw.afternoon_status || null,
      morning_marked_at: raw.morning_marked_at || null,
      afternoon_marked_at: raw.afternoon_marked_at || null,
      morning_lat: raw.morning_lat ?? null,
      morning_lng: raw.morning_lng ?? null,
      afternoon_lat: raw.afternoon_lat ?? null,
      afternoon_lng: raw.afternoon_lng ?? null,
      marked_by: raw.marked_by ?? null,
    };
    if (!payload.student_id || !payload.school_id || !payload.attendance_date) {
      throw new Error("Student attendance sync is missing pupil, school, or date information");
    }
    const { error } = await supabase
      .from("student_attendance")
      .upsert([payload], { onConflict: "student_id,attendance_date" });
    if (error) throw error;
  } else if (entry.op === "upsert_teacher_attendance") {
    const raw = { ...(entry.payload as any) };
    const kindHint: "arrival" | "departure" | undefined = raw._kind;
    delete raw._kind;
    const [teacherIdFromKey, dateFromKey] = entry.row_key.split("_");
    const teacher_user_id = raw.teacher_user_id ?? raw.user_id ?? teacherIdFromKey;
    const school_id = raw.school_id ?? schoolId;
    const attendance_date = raw.attendance_date ?? dateFromKey;
    if (!teacher_user_id || !school_id || !attendance_date) {
      throw new Error("Teacher attendance sync is missing teacher, school, or date information");
    }

    // Fetch the existing server row so we never overwrite the *other* half
    // (e.g. wiping arrival_time when only departure was marked offline).
    const { data: existing, error: fetchErr } = await supabase
      .from("teacher_attendance")
      .select("*")
      .eq("teacher_user_id", teacher_user_id)
      .eq("attendance_date", attendance_date)
      .maybeSingle();
    if (fetchErr) throw fetchErr;

    const merged: Record<string, unknown> = {
      ...(existing ?? {}),
      ...raw, // local fields win for whichever side was just marked
      teacher_user_id,
      school_id,
      attendance_date,
    };
    // If the local payload only touched one side, make sure we DO NOT include
    // null overwrites for the other side. Strip keys that local didn't set.
    if (kindHint === "arrival") {
      delete merged.departure_time;
      delete merged.departure_lat;
      delete merged.departure_lng;
      delete merged.departure_status;
      // Preserve server's departure values if any
      if (existing) {
        merged.departure_time = (existing as any).departure_time ?? null;
        merged.departure_lat = (existing as any).departure_lat ?? null;
        merged.departure_lng = (existing as any).departure_lng ?? null;
        merged.departure_status = (existing as any).departure_status ?? null;
      }
    } else if (kindHint === "departure") {
      delete merged.arrival_time;
      delete merged.arrival_lat;
      delete merged.arrival_lng;
      delete merged.arrival_status;
      if (existing) {
        merged.arrival_time = (existing as any).arrival_time ?? null;
        merged.arrival_lat = (existing as any).arrival_lat ?? null;
        merged.arrival_lng = (existing as any).arrival_lng ?? null;
        merged.arrival_status = (existing as any).arrival_status ?? null;
      }
    }
    // Drop server-managed metadata so upsert doesn't fight triggers
    delete (merged as any).created_at;
    delete (merged as any).updated_at;

    const { error } = await supabase
      .from("teacher_attendance")
      .upsert([merged], { onConflict: "teacher_user_id,attendance_date" });
    if (error) throw error;

  } else {
    throw new Error(`Unknown outbox op: ${entry.op}`);
  }
}

async function pullDeltas(schoolId: string) {
  const meta = await getMeta();
  const sSince = meta.student_attendance_pulled_at ?? "1970-01-01T00:00:00Z";
  const tSince = meta.teacher_attendance_pulled_at ?? "1970-01-01T00:00:00Z";

  // Pull rows updated on the server since our last successful pull. We don't
  // overwrite a row that still has a pending outbox entry — the push will win.
  const pendingKeys = new Set((await listOutbox()).map((e) => e.row_key));

  const [{ data: sa }, { data: ta }] = await Promise.all([
    supabase
      .from("student_attendance")
      .select("*")
      .eq("school_id", schoolId)
      .gt("updated_at", sSince)
      .order("updated_at", { ascending: true })
      .limit(1000),
    supabase
      .from("teacher_attendance")
      .select("*")
      .eq("school_id", schoolId)
      .gt("updated_at", tSince)
      .order("updated_at", { ascending: true })
      .limit(1000),
  ]);

  const sRows: CachedStudentAttendance[] = (sa ?? [])
    .map(
      (r: any) =>
        ({ ...r, local_key: `${r.student_id}_${r.attendance_date}` }) as CachedStudentAttendance,
    )
    .filter((r: CachedStudentAttendance) => !pendingKeys.has(r.local_key));
  const tRows: CachedTeacherAttendance[] = (ta ?? [])
    .map(
      (r: any) =>
        ({
          ...r,
          user_id: r.teacher_user_id,
          local_key: `${r.teacher_user_id}_${r.attendance_date}`,
        }) as CachedTeacherAttendance,
    )
    .filter((r: CachedTeacherAttendance) => !pendingKeys.has(r.local_key));

  if (sRows.length) await bulkUpsertStudentAttendance(sRows);
  if (tRows.length) await bulkUpsertTeacherAttendance(tRows);

  const newest = (arr: { updated_at?: string | null }[]) =>
    arr.reduce<string | null>(
      (acc, r) => (r.updated_at && (!acc || r.updated_at > acc) ? r.updated_at : acc),
      null,
    );
  const newSa = newest(sa ?? []);
  const newTa = newest(ta ?? []);
  await setMeta({
    student_attendance_pulled_at: newSa ?? meta.student_attendance_pulled_at,
    teacher_attendance_pulled_at: newTa ?? meta.teacher_attendance_pulled_at,
  });
}

export async function syncNow(schoolId?: string | null): Promise<void> {
  if (inFlight) {
    // Coalesce: remember that another pass is needed once the current one ends.
    rerunRequested = true;
    if (schoolId) rerunSchoolId = schoolId;
    return inFlight;
  }
  if (!state.online) {
    setState({ pending: (await listOutbox()).length });
    return;
  }
  inFlight = (async () => {
    setState({ syncing: true, lastError: null });
    let firstError: string | null = null;
    try {
      // 1) Drain outbox FIFO — keep going on per-entry failure so one bad
      // row doesn't block the rest of the queue.
      const entries = await listOutbox();
      for (const entry of entries) {
        try {
          await pushOne(entry, schoolId);
          await removeOutboxEntry(entry);
        } catch (err: any) {
          const msg = errorText(err);
          await markOutboxFailure(entry, msg);
          // Hide transient network failures from the UI — they just mean
          // we'll retry next time the device comes back online.
          if (!firstError && !isTransientNetworkError(msg)) firstError = msg;
          if (isTransientNetworkError(msg)) {
            // No point hammering Supabase for the remaining entries — we're offline.
            setState({ online: false });
            break;
          }
        }
      }
      // 2) Pull deltas (only if we know the school)
      if (schoolId) {
        try {
          await pullDeltas(schoolId);
        } catch (err: any) {
          const msg = errorText(err);
          if (!isTransientNetworkError(msg)) {
            if (!firstError) firstError = msg;
          } else {
            setState({ online: false });
          }
        }
      }
      setState({
        lastSyncedAt: new Date().toISOString(),
        lastError: firstError,
      });
    } catch (err: any) {
      const msg = errorText(err);
      setState({ lastError: isTransientNetworkError(msg) ? null : msg });
      if (isTransientNetworkError(msg)) setState({ online: false });
    } finally {
      setState({ syncing: false, pending: (await listOutbox()).length });
      inFlight = null;
      // If marks landed in the outbox while we were syncing, drain them now.
      if (rerunRequested) {
        const nextSchool = rerunSchoolId ?? schoolId ?? null;
        rerunRequested = false;
        rerunSchoolId = null;
        if ((await listOutbox()).length > 0) {
          void syncNow(nextSchool);
        }
      }
    }
  })();
  return inFlight;
}

export async function refreshPendingCount() {
  setState({ pending: (await listOutbox()).length });
}

/** Wire browser + Capacitor lifecycle events to trigger sync. Safe to call many times. */
export function startSyncEngine(getSchoolId: () => string | null | undefined) {
  schoolIdGetter = getSchoolId;
  if (started) return;
  started = true;

  const trigger = () => {
    void syncNow(schoolIdGetter?.() ?? null);
  };

  // Browser online/offline
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      setState({ online: true });
      trigger();
    });
    window.addEventListener("offline", () => setState({ online: false }));
    window.addEventListener("focus", trigger);
  }

  // Capacitor network + app resume (no-op on web)
  (async () => {
    try {
      const { Network } = await import("@capacitor/network");
      const status = await Network.getStatus();
      setState({ online: status.connected });
      Network.addListener("networkStatusChange", (s) => {
        setState({ online: s.connected });
        if (s.connected) trigger();
      });
    } catch {
      /* not in native */
    }
    try {
      const { App } = await import("@capacitor/app");
      App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) trigger();
      });
    } catch {
      /* not in native */
    }
  })();

  // Light interval while foregrounded
  if (typeof window !== "undefined") {
    setInterval(() => {
      if (document.visibilityState === "visible" && state.online) trigger();
    }, 60_000);
  }

  // Initial pending count + first attempt
  void refreshPendingCount();
  trigger();
}
